/**
 * React Query mutation hook for deleting a payment request
 * Handles payment request deletion with automatic query invalidation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deletePaymentRequest } from 'services/billingServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Hook to delete a payment request using React Query mutation
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback function called on successful deletion
 * @param {Function} options.onError - Callback function called on deletion error
 *
 * @returns {Object} Mutation object
 * @returns {Function} returns.mutate - Function to trigger the mutation
 * @returns {Function} returns.mutateAsync - Async function to trigger the mutation
 * @returns {boolean} returns.isLoading - Loading state
 * @returns {boolean} returns.isError - Error state
 * @returns {Object|null} returns.error - Error object if mutation failed
 *
 * @example
 * const deletePaymentRequestMutation = useDeletePaymentRequestQuery({
 *   onSuccess: () => {
 *     console.log('Payment request deleted successfully');
 *   },
 *   onError: (error) => {
 *     console.error('Failed to delete payment request:', error);
 *   },
 * });
 *
 * // Usage
 * deletePaymentRequestMutation.mutate({
 *   payment_request_sk: 'PAYMENT_REQUEST#123',
 *   is_lead: false,
 * });
 */
export const useDeletePaymentRequestQuery = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payment_request_sk, is_lead = false }) => {
      return deletePaymentRequest(payment_request_sk, is_lead);
    },
    onSuccess: (data, variables, context) => {
      // Invalidate all payment request queries to refresh the list
      queryClient.invalidateQueries({ queryKey: queryKeys.paymentRequests.all });

      // Call custom onSuccess callback if provided
      if (onSuccess) {
        onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Call custom onError callback if provided
      if (onError) {
        onError(error, variables, context);
      }
    },
  });
};

export default useDeletePaymentRequestQuery;
